import actions from '@wc-stripe/actions';
import {getSetting} from '@wc-stripe/utils';

/**
 * Gateways that don't support 'subscriptions' (@see WooCommerceSubscriptionsTrait) still receive
 * mode: 'setup'/'subscription' from PaymentIntent, since that PHP-side decision is made without
 * knowing which specific gateway will render. Manual renewals don't require automatic reuse though
 * - WooCommerce Subscriptions just emails the customer to pay renewals manually - so those gateways
 * fall back here to mode: 'payment', rather than the core gateway classes needing to know anything
 * about subscriptions.
 */
function isIncompatibleSubscriptionGateway(gateway) {
    const mode = gateway.settings.elementOptions?.mode;
    if (mode !== 'setup' && mode !== 'subscription') {
        return false;
    }
    return !getSetting('paymentMethods')?.[gateway.id]?.supports?.includes('subscriptions');
}

/**
 * mode: 'payment' requires a positive amount. When the live total is 0 (e.g. a free trial), fall
 * back to the recurring cart total (PaymentIntent::add_recurring_total_to_cart_data()) so the
 * element can still mount - nothing is actually charged today either way
 * (@see WC_Payment_Gateway_Stripe::process_zero_total_order()). The live cart's line items alone
 * aren't reliable here: shipping/tax on them reflect today's (trial-zeroed) totals, while the
 * recurring cart total reflects what will actually be charged going forward.
 */
function getFallbackAmountCents(gateway) {
    return gateway.controller.getTotalCents() || gateway.controller.cart?.data?.recurringTotalCents || 0;
}

actions.addFilter('can_make_payment', (canMakePayment, gateway) => {
    if (canMakePayment && isIncompatibleSubscriptionGateway(gateway)) {
        return getFallbackAmountCents(gateway) > 0;
    }
    return canMakePayment;
});

actions.addFilter('stripe_element_options', (options, gateway) => {
    if (isIncompatibleSubscriptionGateway(gateway)) {
        options.mode = 'payment';
        options.amount = getFallbackAmountCents(gateway);
    }
    return options;
});